import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const PRIORITY_COLORS = {
  high: "#dc2626",
  medium: "#d97706",
  low: "#059669",
};
const PRIORITY_BG = {
  high: "#fef2f2",
  medium: "#fffbeb",
  low: "#ecfdf5",
};

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload[0]) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-2.5 shadow-xl text-sm">
      <p className="text-slate-700 capitalize">{payload[0].payload.name}: <span className="font-bold">{payload[0].value}</span></p>
    </div>
  );
}

export default function PriorityChart({ data }) {
  const navigate = useNavigate();

  if (!data || data.length === 0) return null;

  const sortOrder = { high: 0, medium: 1, low: 2 };
  const sorted = [...data].sort((a, b) => (sortOrder[a.name] ?? 3) - (sortOrder[b.name] ?? 3));

  return (
    <div className="corp-card animate-fade-slide-up" style={{ animationDelay: "600ms" }} data-testid="priority-chart">
      <div className="h-1 w-full bg-[#dc2626]" />
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-base font-bold text-slate-800">By Priority</h3>
          <p className="text-xs text-slate-400 mt-0.5">Severity breakdown</p>
        </div>

        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={sorted} layout="vertical" onClick={(e) => {
            if (e?.activePayload?.[0]?.payload?.name) {
              navigate(`/tickets?priority=${e.activePayload[0].payload.name}`);
            }
          }}>
            <XAxis
              type="number"
              tick={{ fill: '#94a3b8', fontSize: 11, fontFamily: 'DM Sans' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: '#475569', fontSize: 12, fontFamily: 'DM Sans', fontWeight: 500, textTransform: 'capitalize' }}
              axisLine={false}
              tickLine={false}
              width={65}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
            <Bar dataKey="value" radius={[0, 8, 8, 0]} cursor="pointer" barSize={28}>
              {sorted.map((entry) => (
                <Cell key={entry.name} fill={PRIORITY_COLORS[entry.name] || '#94a3b8'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <div className="mt-4 flex gap-3 justify-center">
          {sorted.map((p) => (
            <div
              key={p.name}
              className="flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: PRIORITY_BG[p.name] || '#f8fafc' }}
              onClick={() => navigate(`/tickets?priority=${p.name}`)}
            >
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[p.name] || '#94a3b8' }} />
              <span className="text-xs font-medium capitalize" style={{ color: PRIORITY_COLORS[p.name] }}>{p.name}</span>
              <span className="text-xs font-bold" style={{ color: PRIORITY_COLORS[p.name] }}>{p.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
